import type { CustomWindow } from '@/misc/custom-window'
import Swal from '@/includes/swal'
import { Toastr, type ToastrOptions } from '@/includes/toastr'
import merge from 'lodash/merge'
import type { AddressType } from '@/types/Address'
import type Address from '@/types/Address'
import { isNumeric } from '@/helpers/numbers'
import type ModelAddress from '@/types/ModelAddress'
import { AddOn } from '@/enums/AddOn'

declare let window: CustomWindow

if (history.scrollRestoration) {
  history.scrollRestoration = 'manual'
}

$.ajaxSetup({
  headers: { 'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content') },
})

$.event.special.touchstart = {
  setup: function (_, ns, handle: EventListenerOrEventListenerObject | null) {
    this.addEventListener('touchstart', handle, { passive: true })
  },
}

/**
 * ES6 replacement for $(document).ready(() => {}).
 *
 * Deliberately DOMContentLoaded, not `load`: every Vue mount in the app hangs off this helper, and
 * `load` waits for every image on the page, so the server-rendered shell painted first and the
 * mounted content shifted it afterwards. The bundles are deferred, so readyState is already
 * 'interactive' when this runs and the callback fires immediately.
 *
 * @param {() => void} callBack
 */
export const windowReady = (callBack: () => void): void => {
  if (document.readyState !== 'loading') {
    callBack()
  } else {
    document.addEventListener('DOMContentLoaded', callBack)
  }
}

/**
 * This monitors all AJAX calls that have an error response. If a user's
 * session has expired, then the system will return a 401 status,
 * "Unauthorized", which will trigger this listener and so prompt the user if
 * they'd like to be redirected to the login page.
 */
$(document).ajaxError(function (event, jqxhr, settings, exception) {
  if (exception === 'Unauthorized' || jqxhr.status === 419) {
    Swal.fire({
      title: ' ',
      text: 'U dient opnieuw in te loggen, de huidige sessie is verlopen!',
      icon: 'error',
      showCancelButton: false,
      confirmButtonColor: '#F08825',
      confirmButtonText: 'Inloggen',
    })
      .then(() => (window.location.href = '/login'))
      .catch(e => console.error(e))
  }
})

export const destroyEntity = async (entity, id: any, options: any = {}) => {
  const result = await Swal.fire({
    title: undefined,
    text: options.message ?? 'Weet je zeker dat je dit wilt verwijderen?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#ccc',
    confirmButtonText: 'Verwijderen',
    cancelButtonColor: '#F08825',
    cancelButtonText: 'Annuleren',
  })

  if (!result.value) return

  $.ajax({
    headers: { 'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content') },
    type: 'POST',
    data: {
      _method: 'DELETE',
    },
    url: options.url ?? `/api/${entity}/${parseInt(id)}`,
    success: () => {
      if (options.redirect_url) {
        window.location.href = options.redirect_url

        return
      }

      showToast('Verwijderen is succesvol uitgevoerd!')

      const capitalizedEntity = entity.charAt(0).toUpperCase() + entity.slice(1)

      if ($.fn.dataTable.isDataTable('#tbl' + capitalizedEntity)) {
        window['tbl' + capitalizedEntity].ajax.reload(() => ({}), false)
      }
    },
    error: async (e) => {
      let message = e['responseJSON']?.errors?.message ?? e['responseJSON'].message

      if (e.status === 404) {
        message = 'Item is al verwijderd'
      }

      await Swal.fire({
        text: message,
        icon: 'error',
        confirmButtonColor: '#F08825',
        confirmButtonText: 'Sluiten',
      })
    },
  })
}

export const showToast = async (
  message: any,
  type: 'success' | 'info' | 'warning' | 'error' = 'success',
  title: string | null = null,
  timeOut = 7000,
  additionalOptions: ToastrOptions = {},
) => {
  const toastr = new Toastr()

  const defaults = {
    closeButton: true,
    newestOnTop: true,
    progressBar: true,
    preventDuplicates: false,
    onclick: null,
    showDuration: 300,
    hideDuration: 1000,
    timeOut: timeOut,
    extendedTimeOut: timeOut,
    positionClass: 'toast-bottom-right',
    showEasing: 'swing',
    hideEasing: 'linear',
    showMethod: 'fadeIn',
    hideMethod: 'fadeOut',
  }

  toastr.setOptions(merge({}, defaults, additionalOptions))

  switch (type) {
    case 'error':
      return toastr.error(message, title)
    case 'warning':
      return toastr.warning(message, title)
    case 'info':
      return toastr.info(message, title)
    case 'success':
    default:
      return toastr.success(message, title)
  }
}

export const convertMinutesToTimeFormat = (minutes: number): string => {
  if (!minutes || isNaN(minutes)) return '00:00'

  const totalMinutes = Math.round(Number(minutes) * 60) / 60
  const hour = Math.floor(totalMinutes / 60).toString().padStart(2, '0')
  const minute = Math.floor(totalMinutes % 60).toString().padStart(2, '0')

  return `${hour}:${minute}`
}

export const convertDecimalHoursToMinutes = (decimal: number): number | null => {
  if (!decimal) return null

  const n = new Date(0, 0)

  n.setSeconds(+decimal * 60 * 60)

  const hours = n.getHours() ? n.getHours() * 60 : 0

  return hours + n.getMinutes()
}

export const actionsVisible = (model: string): boolean => {
  if (!window.permissions) return false

  return can(`view ${model}`) ||
    can(`update ${model}`) ||
    can(`delete ${model}`) ||
    can(`archive ${model}`)
}

export const can = (permission: string): boolean => {
  if (!window.permissions) return false

  return window.permissions.includes(permission)
}

export const cannot = (permission: string): boolean => {
  return !can(permission)
}

export const hasSubscription = (subscription: number): boolean => {
  return window.subscription_plan >= subscription
}

export function isLicensedTo(license: string): boolean {
  if (license === 'none') return true

  if (!window.licenses) return false

  return window.licenses.includes(license)
}

export function hasAddOn(addOn: number): boolean {
  if (!window.add_ons) return false

  return window.add_ons.includes(addOn)
}

export const hasAuthorizedPtv = (): boolean => hasAddOn(AddOn.PTV) && !!window.ptv_api_key

export const determineAddressType = (address: Partial<Address> | null): AddressType => {
  if (!address || !address?.city) return 'routes' satisfies AddressType

  if (address.city && address.street && address.house_number) return 'addresses' satisfies AddressType

  if (address.street) return 'streets' satisfies AddressType

  return 'cities' satisfies AddressType
}

export const fullAddressWithLabel = (address: Address | null, addressable: ModelAddress | null, asHtml: boolean = false): string => {
  if (!address) return ''

  if (!addressable || !addressable.label) return address.full_address

  return asHtml ?
    `<span>${address.full_address}</span> <span class="text-muted">[${addressable.label}]</span>` :
    `${address.full_address} [${addressable.label}]`
}

export function addressLabel(address: Partial<Address>, preferredCountryCode: string = 'NL') {
  const type = determineAddressType(address)
  const street = address.street ? address.street.trim() : ''
  const houseNumber = address.house_number ? address.house_number.trim() : ''
  const houseNumberAdditions = address.house_number_additions ? address.house_number_additions.trim() : ''
  const city = address.city ? address.city.trim() : ''
  const province = address.province ? address.province.trim() : ''
  const municipality = address.municipality ? address.municipality.trim() : ''
  const countryCode = address.country_code ? address.country_code.trim() : 'NL'
  const countryName = address.country_name ? address.country_name.trim() : 'Nederland'

  let baseAddress = ''

  switch (type) {
    case 'routes':
      baseAddress = `${street}, ${countryName}`
      break
    case 'addresses':
      baseAddress = [`${street} ${houseNumber}${isNumeric(houseNumberAdditions) ? `-${houseNumberAdditions}` : houseNumberAdditions?.toUpperCase()}`.trim(), city].join(', ')
      break
    case 'streets':
      baseAddress = `${street}, ${city}`
      break
    case 'cities':
      baseAddress = city
      break
  }

  const addressParts = [baseAddress]

  const isForeignCountry = countryCode !== preferredCountryCode

  if (isForeignCountry && ((province && city !== province) || (municipality && city !== municipality) || countryName) && type !== 'routes') {
    const foreignAddressParts: string[] = []

    if (municipality && city !== municipality) foreignAddressParts.push(municipality)

    if (province && city !== province && !foreignAddressParts.includes(province)) foreignAddressParts.push(province)

    if (countryName) foreignAddressParts.push(foreignAddressParts.length ? `, ${countryName}` : countryName)

    const foreignAddressString = foreignAddressParts.join(' ')
      .replace(' , ', ', ')
      .trim()

    addressParts.push(`(${foreignAddressString})`)
  }

  return addressParts.join(' ')
    .replace('  ', ' ')
    .trim()
}

windowReady(() => {
  $('.kt-menu__item--active').closest('.kt-menu__item--submenu').addClass('kt-menu__item--open')

  $('.input--group--buttons button').on('click', function () {
    $(this)
      .closest('.input--group--buttons')
      .find('button')
      .removeClass('active')

    $(this).addClass('active')
  })

  $(document).on('keypress', function (e) {
    const el = $(e.target)
    const selector = `#${el.attr('id')}`

    // Prevent triggering a save when were inside a dataTable Editor.
    if (
      e.key === 'Enter' &&
      el.attr('id') &&
      $('table.dataTable').find(selector).length === 1
    ) {
      return true
    }

    if (e.key === 'Enter' && !$('*:focus').is('textarea') && !$('*:focus').is('input[type="search"]')) {
      // If in modal only try to save the included form
      const visibleModal = $('.modal.show')
      const genericVueModal = $('.generic-modal')

      if (genericVueModal?.length > 0) {
        return true
      }

      if (visibleModal.length) {
        $('.show > .modal-dialog > .modal-content > .modal-footer > .d-flex > .text-right > .btn--primary')
          .first()
          .trigger('click')
        $(visibleModal)
          .find('.modal-footer > .btn--primary')
          .trigger('click')
      }

      if (!visibleModal.length) {
        // Descendant selector: the subheader renders the button group through a portal-target wrapper
        const btnPrimary = $('.kt-subheader__toolbar .btn-dropdown-group > .btn--primary:not(.disable-enter)').first()

        if (btnPrimary.length) {
          btnPrimary.trigger('click')
        }
      }
    }
  })

  $(document).on('change', '.is-invalid', function () {
    const input_field = $(this)

    if (input_field.hasClass('input-vue')) return

    if (input_field.hasClass('is-invalid')) {
      input_field.removeClass('is-invalid')
    }

    // If in put lives inside tab and all tab errors are solved, remove error marking
    if (input_field.closest('.tab-pane').length) {
      const parent_tab_panel = input_field.closest('.tab-pane')

      if (parent_tab_panel.find('.' + 'is-invalid').length === 0) {
        $(`.nav-tabs a[href="#${parent_tab_panel.attr('id')}"]`).removeClass('invalid-fields')
      }
    }
  })

  $('#returnBack').on('click', function () {
    const updateAndReturnBtn = $('.btn-dropdown-group').find('a[id^=\'updateAndReturn\']')

    if (updateAndReturnBtn.length !== 0) {
      sessionStorage['id_last_modified'] = $(updateAndReturnBtn).data('id_last_modified')
      sessionStorage['redirect_url'] = $(updateAndReturnBtn).data('redirect_url')
    } else if (
      $(this).data('id_last_modified') &&
      $(this).data('redirect_url')
    ) {
      sessionStorage['id_last_modified'] = $(this).data('id_last_modified')
      sessionStorage['redirect_url'] = $(this).data('redirect_url')
    }
  })
})

window['can'] = can
window['cannot'] = cannot
window['isLicensedTo'] = isLicensedTo

window.showToast = showToast
window.windowReady = windowReady
